import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { ImageType } from 'react-images-uploading';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Button as MIButton } from '@material-ui/core';
import { ErrorInfo, isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { BlogUiModel } from '../../../shared/ui-models/blog.ui-model';
import { APP_ROUTES } from '../../../shared/config/routes-config';
import { RouteConfig } from '../../../shared/interfaces/routes-config.interface';
import { birdToast } from '../../../shared/components/core-ui/toast';
import {
  addNewBlog,
  getBlog,
  updateBlog,
  uploadBlogImage
} from '../../shared/services/data.service';
import { EditBlogContent } from './components/edit-blog-content.container';
import { EditBlogContentArabic } from './components/edit-blog-content-ar.container';

type BlogTab = 'en' | 'ar';

export const EditBlogContainer = () => {
  const { id } = useParams<{ id?: string }>();
  const history = useHistory();
  const { t } = useTranslation();

  const isNew = !id;
  const [tab, setTab] = useState<BlogTab>('en');
  const [blog, setBlog] = useState<BlogUiModel | undefined>(isNew ? {} : undefined);
  const [isLoading, setIsLoading] = useState<boolean>(!isNew);
  const [pendingImages, setPendingImages] = useState<{ en?: File; ar?: File }>({});
  const [draft, setDraft] = useState<BlogUiModel>({});

  useEffect(() => {
    if (!id) return;

    setIsLoading(true);
    getBlog(id).then((response: BlogUiModel | ErrorInfo) => {
      setIsLoading(false);
      if (isErrorInfo(response)) {
        birdToast.error(t('BLOG_MODULE.EDIT_CONTAINER.NOTIFICATIONS.LOAD_FAILED.MESSAGE'));
        return;
      }
      setBlog(response);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleCreate = (payload: BlogUiModel) => {
    if (!(payload.header && payload.headerAr && payload.body && payload.bodyAr)) {
      birdToast.info(t('BLOG_MODULE.EDIT_CONTAINER.NOTIFICATIONS.BOTH_LANGUAGES_REQUIRED.MESSAGE'));
      return;
    }

    const promise = addNewBlog(payload).then(async (response: BlogUiModel | ErrorInfo) => {
      if (isErrorInfo(response)) {
        throw response;
      }

      const createdId = String(response.id);

      for (const locale of ['en', 'ar'] as ('en' | 'ar')[]) {
        const file = pendingImages[locale];
        if (!file) continue;

        const uploaded = await uploadBlogImage(file, createdId, locale);
        if (isErrorInfo(uploaded)) {
          birdToast.error(t('BLOG_MODULE.EDIT_CONTAINER.NOTIFICATIONS.IMAGE_FAILED.MESSAGE'));
        }
      }

      setPendingImages({});
      setDraft({});
      history.replace((APP_ROUTES.BLOG_MODULE.CHILDREN as RouteConfig).LIST.FULL_PATH);
    });

    birdToast.promise(promise, {
      loadingMessage: 'Creating blog...',
      successMessage: t('BLOG_MODULE.EDIT_CONTAINER.NOTIFICATIONS.CREATE_SUCCESS.MESSAGE'),
      errorPermissionMessage: t(
        'BLOG_MODULE.EDIT_CONTAINER.NOTIFICATIONS.CREATE_PERMISSION.MESSAGE'
      ),
      errorFailedMessage: t('BLOG_MODULE.EDIT_CONTAINER.NOTIFICATIONS.CREATE_FAILED.MESSAGE')
    });
  };

  const handleValuesChange = (values: Partial<BlogUiModel>) => {
    setDraft((current) => ({ ...current, ...values }));
  };

  const handleSubmit = (updatedData: Partial<BlogUiModel>) => {
    if (isNew || !id) {
      handleCreate({ ...draft, ...updatedData });
      return;
    }

    const promise = updateBlog(id, updatedData).then((response: BlogUiModel | ErrorInfo) => {
      if (isErrorInfo(response)) {
        throw response;
      }
      setBlog(response);
    });

    birdToast.promise(promise, {
      loadingMessage: 'Updating blog...',
      successMessage: t('BLOG_MODULE.EDIT_CONTAINER.NOTIFICATIONS.UPDATE_SUCCESS.MESSAGE'),
      errorPermissionMessage: t(
        'BLOG_MODULE.EDIT_CONTAINER.NOTIFICATIONS.UPDATE_PERMISSION.MESSAGE'
      ),
      errorFailedMessage: t('BLOG_MODULE.EDIT_CONTAINER.NOTIFICATIONS.UPDATE_FAILED.MESSAGE')
    });
  };

  const handleImageUpload = (image: ImageType, locale: 'en' | 'ar') => {
    if (!(image && image.file)) return;

    if (!id) {
      const file = image.file;
      setPendingImages((current) => ({ ...current, [locale]: file }));
      birdToast.info(t('BLOG_MODULE.EDIT_CONTAINER.NOTIFICATIONS.IMAGE_PENDING.MESSAGE'));
      return;
    }

    const promise = uploadBlogImage(image.file, id, locale).then(
      (response: BlogUiModel | ErrorInfo) => {
        if (isErrorInfo(response)) {
          throw response;
        }
        setBlog(response);
      }
    );

    birdToast.promise(promise, {
      loadingMessage: 'Uploading image...',
      successMessage: t('BLOG_MODULE.EDIT_CONTAINER.NOTIFICATIONS.IMAGE_SUCCESS.MESSAGE'),
      errorPermissionMessage: t(
        'BLOG_MODULE.EDIT_CONTAINER.NOTIFICATIONS.IMAGE_PERMISSION.MESSAGE'
      ),
      errorFailedMessage: t('BLOG_MODULE.EDIT_CONTAINER.NOTIFICATIONS.IMAGE_FAILED.MESSAGE')
    });
  };

  const tabClassName = (tabKey: BlogTab) =>
    `cursor-pointer border-b-2 px-[1.5rem] py-[0.8rem] text-[1.6rem] font-bold ${
      tab === tabKey ? 'border-primary-500 text-primary-500' : 'border-transparent text-gray-500'
    }`;

  return (
    <div className="flex h-full flex-col items-start justify-start md:min-h-[calc(100vh-45px)] md:bg-[#f5f7fc] md:px-[2rem] md:py-[3rem]">
      <div className="flex w-full items-center justify-between gap-[1rem]">
        <div className="flex items-center gap-[0.5rem]">
          <MIButton
            aria-label={t('BLOG_MODULE.EDIT_CONTAINER.BACK')}
            onClick={() => {
              history.push((APP_ROUTES.BLOG_MODULE.CHILDREN as RouteConfig).LIST.FULL_PATH);
            }}
          >
            <ArrowBackIcon fontSize="large" color="inherit" />
          </MIButton>
          <div className="my-[1rem] text-[2.5rem] font-bold text-primary-500">
            {isNew
              ? t('BLOG_MODULE.EDIT_CONTAINER.NEW_TITLE')
              : t('BLOG_MODULE.EDIT_CONTAINER.EDIT_TITLE')}
          </div>
        </div>
      </div>

      <div className="mb-[2rem] flex w-full items-center gap-[1rem] border-b border-gray-200">
        <div className={tabClassName('en')} onClick={() => setTab('en')}>
          {t('BLOG_MODULE.EDIT_CONTAINER.TAB_EN')}
        </div>
        <div className={tabClassName('ar')} onClick={() => setTab('ar')}>
          {t('BLOG_MODULE.EDIT_CONTAINER.TAB_AR')}
        </div>
      </div>

      <div className="w-full rounded-[4px] bg-white p-[2rem] shadow-[0_2px_4px_0_rgba(187,186,186,0.5)]">
        <div className={tab === 'en' ? 'block' : 'hidden'}>
          <EditBlogContent
            data={{ blog }}
            config={{ isLoading, isNew }}
            eventHandlers={{
              submitHandler: handleSubmit,
              valuesChangeHandler: handleValuesChange,
              imageUploadHandler: (file: ImageType) => handleImageUpload(file, 'en')
            }}
          />
        </div>
        <div className={tab === 'ar' ? 'block' : 'hidden'}>
          <EditBlogContentArabic
            data={{ blog }}
            config={{ isLoading, isNew }}
            eventHandlers={{
              submitHandler: handleSubmit,
              valuesChangeHandler: handleValuesChange,
              imageUploadHandler: (file: ImageType) => handleImageUpload(file, 'ar')
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default EditBlogContainer;
